import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { ReportScheduleModal } from './ReportScheduleModal';
import { useNavbarActions } from '../../context/NavbarActionsContext';
import { useReportDepartmentAccess } from '../../hooks/useReportAccess';
import { useTheme } from '../../context/ThemeContext';
import { currencySymbol } from '../../utils/currency';
import './ReportsNative.css';

/**
 * Applied-currency chip. Rendered only for non-USD currencies (USD is the
 * implicit default and needs no indicator). Shows "EUR €" so a viewer always
 * knows which currency the numbers on the page are expressed in.
 */
/**
 * Share the current report view: the URL already encodes filters, drill
 * expansions and the `sy` scroll offset, so the recipient lands on the exact
 * same spot. Prefers the native share bridge (mobile webview), falls back to
 * clipboard + a lightweight toast. Registered as a shell-header action.
 */
export async function shareReportView({ category, title, subtitle } = {}) {
  const url = window.location.href;
  if (typeof window.sendNativeReportShareMessage === 'function') {
    try {
      await window.sendNativeReportShareMessage({
        url,
        reportCategory: category || 'Reports',
        reportTitle: title || document.title || 'Report',
        subtitle: subtitle || '',
      });
      return;
    } catch (e) {
      // fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    // lightweight toast (no dependency on a toast provider)
    const el = document.createElement('div');
    el.textContent = 'Link copied — paste it in chat to share this exact view';
    el.style.cssText =
      'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--lodge-onyx-900);color:#fff;' +
      'padding:10px 18px;border-radius:9999px;font-size:13px;z-index:9999;box-shadow:0 8px 24px rgba(15,15,17,.25)';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2600);
  } catch (e) {
    window.prompt('Copy this link to share the exact view:', url);
  }
}

/**
 * Partial-view scope chip. Rendered only when the caller's department grants
 * narrow this report's dimension (RLS): "🔒 4 of 11 depts", with a
 * hover/focus popover naming the included departments — so a restricted user
 * always knows their totals are a subset, without an alarming banner.
 */
function ScopeChip({ kind }) {
  const departmentAccess = useReportDepartmentAccess();
  const summary = departmentAccess?.[kind];
  if (!summary?.restricted) return null;
  const names = Array.isArray(summary.assignedNames) ? summary.assignedNames : [];
  return (
    <span className="rn-scope-chip" tabIndex={0} aria-label="Your view of this report is limited to your assigned departments">
      <Lock aria-hidden="true" />
      {summary.assignedCount} of {summary.totalCount} depts
      <span className="rn-scope-pop" role="tooltip">
        <b>Your view is limited to:</b>
        <ul>
          {names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
        Totals on this report reflect only these departments.
      </span>
    </span>
  );
}

function CurrencyChip({ currency }) {
  if (!currency || typeof currency !== 'string') return null;
  const code = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code) || code === 'USD') return null;
  const symbol = currencySymbol(code);
  return (
    <span className="rn-ccy-chip" title={`All values on this report are shown in ${code}`}>
      {code}
      {symbol && symbol !== code ? <b>{symbol}</b> : null}
    </span>
  );
}

/**
 * Page chrome for native report pages (per financial-pnl.html header):
 * REPORTS · <category> crumb, Geist semibold h1, subtitle line,
 * right-side action buttons.
 *
 * Props:
 *   category          crumb highlight, e.g. 'Financial' (renders "Reports · Financial")
 *   title             page h1, e.g. 'Profit & Loss'
 *   subtitle          string or node — "Coho Services · 24 properties · March – June 2026"
 *   onExport          show Export button (label via exportLabel, default 'Export')
 *   onScheduleEmail   show Schedule email button (custom handler)
 *   scheduleEmail     { organizationId, orgName, userEmail, reportView } — self-hosted
 *                     Schedule email button + subscription modal (PBI
 *                     subscription replacement); ignored when onScheduleEmail
 *                     is also provided
 *   onRefresh         show primary Refresh button (label via refreshLabel)
 *   isRefreshing      spins the refresh icon + disables the button
 *   tabs              optional tab-strip node, rendered inline after the title
 *   restrictionKind   'coa' | 'payroll' — which department dimension gates
 *                     this report ('coa' for financial pages, 'payroll' for
 *                     labor); shows the partial-view scope chip when the
 *                     caller's grants narrow it
 *   selection         active-selection indicator (CrossFilterChip). Renders
 *                     in the header's scope cluster on purpose: that row is
 *                     sized by the h1, so a selection can appear and clear
 *                     without shifting the page content below it
 *   actions           extra node(s) rendered at the end of the title cluster
 *   headerActions     page-specific actions for the shell header's icon
 *                     cluster: [{ key, label, onClick, text?, badge?, disabled? }]
 *                     — `text` renders a labelled button instead of an icon
 *                     (key must have an icon in AppHeader's ACTION_ICONS)
 *   aside             node pinned to the head band's right edge (e.g. the
 *                     dashboard's AI briefing strip)
 *   showPdf           register the PDF action (default true)
 *   showShare         register the Share action (default true)
 *   maxWidth          page max width px (default 2560 — data-dense report
 *                     tables earn the room, so large displays fill instead of
 *                     centering a 1760px column in dead margin; below ~1550px
 *                     viewports the cap is not the binding constraint, and
 *                     only past 2560 logical px does the page stop growing)
 *   children          page body
 *
 * The built-in actions (Excel/PDF/Schedule/Share/Refresh) register with
 * NavbarActionsContext and render as icon buttons in the app shell header —
 * never in the page body.
 */
export function ReportPageShell({
  category,
  title,
  subtitle,
  onExport,
  exportLabel = 'Export',
  onExportPdf,
  isExportingPdf,
  onScheduleEmail,
  scheduleEmail,
  scheduleEmailLabel = 'Schedule email',
  onRefresh,
  refreshLabel = 'Refresh',
  isRefreshing = false,
  currency,
  tabs,
  restrictionKind,
  selection,
  actions,
  headerActions,
  aside,
  showPdf = true,
  showShare = true,
  maxWidth = 2560,
  className,
  children,
}) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { insetActive: inset } = useTheme();
  // Inset shell: the toolbar (ReportFilterBar) renders the head — tabs at its
  // start, a compact "Title · subtitle" + chips + aside at its end — so the
  // filters sit flush under the shell header. It registers itself here; a
  // page without a toolbar gets the compact head band instead.
  const [hasToolbar, setHasToolbar] = useState(false);
  const handleSchedule = onScheduleEmail || (scheduleEmail ? () => setScheduleOpen(true) : null);

  // Page actions live in the sticky shell header. A fresh array per render
  // is intentional — useNavbarActions re-registers only when the visible
  // shape changes and reads handlers through a ref.
  const trayActions = [];
  (headerActions || []).forEach((a) => { if (a && a.key && a.onClick) trayActions.push(a); });
  if (onExport) {
    trayActions.push({ key: 'excel', label: exportLabel === 'Export' ? 'Export to Excel' : exportLabel, onClick: onExport });
  }
  if (showPdf) {
    trayActions.push({
      key: 'pdf',
      label: isExportingPdf ? 'Generating PDF…' : 'Export PDF',
      onClick: () => (onExportPdf ? onExportPdf() : window.print()),
      disabled: isExportingPdf,
      spinning: isExportingPdf,
    });
  }
  if (handleSchedule) {
    trayActions.push({ key: 'schedule', label: scheduleEmailLabel, onClick: handleSchedule });
  }
  if (showShare) {
    trayActions.push({
      key: 'share',
      label: 'Share this exact view (copies a link that keeps filters and drill-downs)',
      onClick: () => shareReportView({ category, title, subtitle: typeof subtitle === 'string' ? subtitle : '' }),
    });
  }
  if (onRefresh) {
    trayActions.push({
      key: 'refresh',
      label: refreshLabel,
      onClick: onRefresh,
      disabled: isRefreshing,
      spinning: isRefreshing,
      primary: true,
    });
  }
  useNavbarActions(trayActions);

  // Head band: title · subtitle · tabs · chips on one left-anchored row,
  // optional aside on the right. Built-in actions live in the shell header;
  // the breadcrumb there carries the section, so no crumb here.
  const chips = (
    <>
      <CurrencyChip currency={currency} />
      {restrictionKind ? <ScopeChip kind={restrictionKind} /> : null}
      {selection}
      {actions}
    </>
  );
  const headCtx = useMemo(
    () => ({ active: inset, title, subtitle, tabs, chips, aside, setHasToolbar }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inset, title, subtitle, tabs, aside, currency, restrictionKind, selection, actions]
  );

  const head = inset ? (
    hasToolbar ? null : (
      <header className="rn-report-head rn-report-head--inset">
        <div className="rn-head-main">
          <div className="rn-head-title">
            <h1>{title}</h1>
            {chips}
          </div>
          {subtitle && <div className="rn-report-sub">{subtitle}</div>}
        </div>
        {aside ? <div className="rn-head-aside">{aside}</div> : null}
        {tabs ? <div className="rn-head-tabs">{tabs}</div> : null}
      </header>
    )
  ) : (
    <header className="rn-report-head">
      <div className="rn-head-title">
        <h1>{title}</h1>
        {subtitle && <div className="rn-report-sub">{subtitle}</div>}
        {tabs}
        <CurrencyChip currency={currency} />
        {restrictionKind ? <ScopeChip kind={restrictionKind} /> : null}
        {selection}
        {actions}
      </div>
      {aside ? <div className="rn-head-aside">{aside}</div> : null}
    </header>
  );

  return (
    <ReportHeadContext.Provider value={headCtx}>
    <div className={`rn-page${className ? ` ${className}` : ''}`} style={{ maxWidth }}>
      {head}
      {children}
      {scheduleEmail && (
        <ReportScheduleModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          organizationId={scheduleEmail.organizationId}
          orgName={scheduleEmail.orgName}
          userEmail={scheduleEmail.userEmail}
          reportView={scheduleEmail.reportView}
        />
      )}
    </div>
    </ReportHeadContext.Provider>
  );
}

const ReportHeadContext = createContext(null);

/**
 * Toolbar side: the page head to render inside the inset toolbar (null when
 * not in the inset shell or outside a ReportPageShell). Registers the toolbar
 * so the shell drops its own head band.
 */
export function useReportHeadInToolbar() {
  const ctx = useContext(ReportHeadContext);
  const active = !!ctx?.active;
  const setHasToolbar = ctx?.setHasToolbar;
  useLayoutEffect(() => {
    if (!active || !setHasToolbar) return undefined;
    setHasToolbar(true);
    return () => setHasToolbar(false);
  }, [active, setHasToolbar]);
  return active ? ctx : null;
}

export default ReportPageShell;
